/**
 * FormattingSpanManager - Manages character-level formatting spans
 *
 * This module provides operations for manipulating formatting spans:
 * - Apply formatting to a range (splits overlapping spans, merges adjacent)
 * - Remove formatting from a range
 * - Adjust spans after text insertion/deletion
 * - Resolve formatting at a position (inheritance chain)
 *
 * All operations maintain span invariants:
 * - Spans are sorted by start index
 * - Adjacent spans with identical formatting are merged
 * - No empty spans (start === end)
 * - No overlapping spans
 *
 * @module FormattingSpanManager
 * @since 2026-01-21
 */

import { v4 as uuidv4 } from 'uuid';
import type {
  FormattingSpan,
  FormattingAttributes,
  ResolvedFormatting
} from '../types/FormattingSpan';
import {
  spansHaveEqualFormatting,
  spanHasFormatting,
  DEFAULT_FORMATTING
} from '../types/FormattingSpan';
import { WasmLogger } from '@/utils/loggers/WasmLogger';

/**
 * FormattingSpanManager - Static class for span operations
 *
 * All methods are pure functions that return new arrays/objects.
 * Input arrays are never mutated.
 */
export class FormattingSpanManager {
  /**
   * Apply formatting to a range within a paragraph
   *
   * This is the primary operation for toolbar formatting actions.
   * Handles splitting existing spans and merging adjacent identical spans.
   *
   * @param spans - Current spans array (not mutated)
   * @param start - Start grapheme index (relative to paragraph)
   * @param end - End grapheme index (exclusive)
   * @param formatting - Formatting properties to apply
   * @returns New spans array (sorted, merged)
   */
  static applyFormatting(
    spans: FormattingSpan[],
    start: number,
    end: number,
    formatting: FormattingAttributes
  ): FormattingSpan[] {
    if (start >= end) {
      WasmLogger.formatting?.spanApply?.({ start, end, skipped: true, reason: 'empty range' });
      return spans;
    }

    WasmLogger.formatting?.spanApply?.({
      start,
      end,
      attrs: Object.keys(formatting),
      beforeCount: spans.length
    });

    // Step 1: Split any spans that overlap with [start, end)
    const splitSpans = this.splitOverlappingSpans(spans, start, end);

    // Step 2: Apply formatting to spans in range, or create new span
    const updatedSpans = this.applyToRange(splitSpans, start, end, formatting);

    // Step 3: Merge adjacent spans with identical formatting
    const mergedSpans = this.mergeAdjacentSpans(updatedSpans);

    // Step 4: Remove empty spans and sort
    const result = mergedSpans
      .filter(s => s.start < s.end && spanHasFormatting(s))
      .sort((a, b) => a.start - b.start);

    WasmLogger.formatting?.spanMerge?.({
      beforeCount: spans.length,
      afterCount: result.length
    });

    return result;
  }

  /**
   * Remove formatting properties from a range
   *
   * @param spans - Current spans array
   * @param start - Start grapheme index
   * @param end - End grapheme index (exclusive)
   * @param properties - Properties to remove (e.g., ['fontWeight', 'fontStyle'])
   * @returns New spans array
   */
  static removeFormatting(
    spans: FormattingSpan[],
    start: number,
    end: number,
    properties: (keyof FormattingAttributes)[]
  ): FormattingSpan[] {
    if (start >= end || properties.length === 0) {
      return spans;
    }

    // Split overlapping spans
    const splitSpans = this.splitOverlappingSpans(spans, start, end);

    // Remove specified properties from spans in range
    const updatedSpans = splitSpans.map(span => {
      // Check if span is in range
      if (span.end <= start || span.start >= end) {
        return span; // Not in range, keep as-is
      }

      // Remove specified properties
      const updated: FormattingSpan = { ...span };
      for (const prop of properties) {
        delete (updated as unknown as Record<string, unknown>)[prop];
      }

      return updated;
    });

    // Filter out spans with no formatting and merge
    return this.mergeAdjacentSpans(updatedSpans)
      .filter(s => s.start < s.end && spanHasFormatting(s))
      .sort((a, b) => a.start - b.start);
  }

  /**
   * Clear all formatting from a range (reset to defaults)
   *
   * @param spans - Current spans array
   * @param start - Start grapheme index
   * @param end - End grapheme index (exclusive)
   * @returns New spans array with formatting removed from range
   */
  static clearFormatting(
    spans: FormattingSpan[],
    start: number,
    end: number
  ): FormattingSpan[] {
    if (start >= end) {
      return spans;
    }

    // Split at boundaries
    const splitSpans = this.splitOverlappingSpans(spans, start, end);

    // Remove spans that are entirely within range, keep others
    return splitSpans
      .filter(span => span.end <= start || span.start >= end)
      .sort((a, b) => a.start - b.start);
  }

  /**
   * Adjust spans after text insertion
   *
   * When text is inserted at a position, spans after that position shift.
   * Spans containing the insertion point expand to include inserted text
   * (inheriting formatting from the containing span).
   *
   * @param spans - Current spans array
   * @param insertPoint - Where text was inserted (grapheme index)
   * @param insertLength - Number of graphemes inserted
   * @param inheritFormatting - If true, inserted text inherits formatting from containing span
   * @returns New spans array with adjusted positions
   */
  static adjustForInsertion(
    spans: FormattingSpan[],
    insertPoint: number,
    insertLength: number,
    inheritFormatting: boolean = true
  ): FormattingSpan[] {
    if (insertLength <= 0) {
      return spans;
    }

    return spans.map(span => {
      if (span.end <= insertPoint) {
        // Span is entirely before insertion - no change
        return span;
      } else if (span.start >= insertPoint) {
        // Span is entirely after insertion - shift
        return {
          ...span,
          start: span.start + insertLength,
          end: span.end + insertLength
        };
      } else {
        // Insertion point is inside span
        if (inheritFormatting) {
          // Expand span to include inserted text
          return {
            ...span,
            end: span.end + insertLength
          };
        } else {
          // Don't expand - inserted text gets no formatting
          // Keep span as-is (it ends at insertPoint logically)
          return span;
        }
      }
    }).filter(s => s.start < s.end);
  }

  /**
   * Adjust spans after text deletion
   *
   * When text is deleted, spans shrink, shift, or are removed.
   *
   * @param spans - Current spans array
   * @param deleteStart - Start of deleted range
   * @param deleteEnd - End of deleted range (exclusive)
   * @returns New spans array with adjusted positions
   */
  static adjustForDeletion(
    spans: FormattingSpan[],
    deleteStart: number,
    deleteEnd: number
  ): FormattingSpan[] {
    if (deleteStart >= deleteEnd) {
      return spans;
    }

    const deleteLength = deleteEnd - deleteStart;

    return spans
      .map(span => {
        if (span.end <= deleteStart) {
          // Span is entirely before deletion - no change
          return span;
        } else if (span.start >= deleteEnd) {
          // Span is entirely after deletion - shift left
          return {
            ...span,
            start: span.start - deleteLength,
            end: span.end - deleteLength
          };
        } else if (span.start >= deleteStart && span.end <= deleteEnd) {
          // Span is entirely within deletion - remove
          return null;
        } else if (span.start < deleteStart && span.end > deleteEnd) {
          // Deletion is entirely within span - shrink
          return {
            ...span,
            end: span.end - deleteLength
          };
        } else if (span.start < deleteStart) {
          // Span overlaps start of deletion - truncate end
          return {
            ...span,
            end: deleteStart
          };
        } else {
          // Span overlaps end of deletion - truncate start and shift
          return {
            ...span,
            start: deleteStart,
            end: span.end - deleteLength
          };
        }
      })
      .filter((s): s is FormattingSpan => s !== null && s.start < s.end);
  }

  /**
   * Resolve formatting at a specific grapheme position
   *
   * Applies inheritance chain: document → paragraph → span
   *
   * @param spans - Paragraph's formatting spans
   * @param position - Grapheme position within paragraph
   * @param paragraphFormatting - Paragraph-level formatting overrides
   * @param documentFormatting - Document-level formatting defaults
   * @returns Fully resolved formatting for the position
   */
  static resolveFormattingAt(
    spans: FormattingSpan[],
    position: number,
    paragraphFormatting: Partial<ResolvedFormatting> = {},
    documentFormatting: ResolvedFormatting = DEFAULT_FORMATTING
  ): ResolvedFormatting {
    // Start with document defaults
    const resolved: ResolvedFormatting = { ...documentFormatting };

    // Apply paragraph-level overrides
    this.applyOverrides(resolved, paragraphFormatting);

    // Find span containing this position
    const span = spans.find(s => s.start <= position && s.end > position);
    if (span) {
      // Apply span-level overrides
      this.applyOverrides(resolved, {
        fontFamily: span.fontFamily,
        fontSizePx: span.fontSizePx,
        fontWeight: span.fontWeight,
        fontStyle: span.fontStyle,
        color: span.color,
        backgroundColor: span.backgroundColor,
        underline: span.underline,
        strikethrough: span.strikethrough,
        superscript: span.superscript,
        subscript: span.subscript,
        features: span.features
      });
    }

    return resolved;
  }

  /**
   * Get formatting at cursor position (for toolbar state display)
   *
   * @param spans - Paragraph's formatting spans
   * @param position - Cursor position within paragraph
   * @returns Partial formatting (only properties that differ from defaults)
   */
  static getFormattingAtCursor(
    spans: FormattingSpan[],
    position: number
  ): Partial<FormattingAttributes> {
    const span = spans.find(s => s.start <= position && s.end > position);
    if (!span) {
      return {};
    }

    const result: Partial<FormattingAttributes> = {};
    if (span.fontFamily !== undefined) result.fontFamily = span.fontFamily;
    if (span.fontSizePx !== undefined) result.fontSizePx = span.fontSizePx;
    if (span.fontWeight !== undefined) result.fontWeight = span.fontWeight;
    if (span.fontStyle !== undefined) result.fontStyle = span.fontStyle;
    if (span.color !== undefined) result.color = span.color;
    if (span.backgroundColor !== undefined) result.backgroundColor = span.backgroundColor;
    if (span.underline !== undefined) result.underline = span.underline;
    if (span.strikethrough !== undefined) result.strikethrough = span.strikethrough;
    if (span.superscript !== undefined) result.superscript = span.superscript;
    if (span.subscript !== undefined) result.subscript = span.subscript;
    if (span.features !== undefined) result.features = span.features;

    return result;
  }

  /**
   * Check if a range has uniform formatting
   * Used to determine toolbar button states for selections
   *
   * @param spans - Paragraph's formatting spans
   * @param start - Start of range
   * @param end - End of range
   * @param property - Property to check
   * @returns { uniform: boolean, value: T | undefined } or 'mixed' if not uniform
   */
  static isFormattingUniform<K extends keyof FormattingAttributes>(
    spans: FormattingSpan[],
    start: number,
    end: number,
    property: K
  ): { uniform: true; value: FormattingAttributes[K] | undefined } | { uniform: false } {
    // Find all spans that overlap with [start, end)
    const overlapping = spans.filter(s => s.end > start && s.start < end);

    if (overlapping.length === 0) {
      // No spans in range - uniform (undefined = inherit)
      return { uniform: true, value: undefined };
    }

    // Check if all overlapping spans have the same value for the property
    const firstValue = overlapping[0][property];
    for (let i = 1; i < overlapping.length; i++) {
      if (overlapping[i][property] !== firstValue) {
        return { uniform: false };
      }
    }

    // Also check gaps between spans (which have undefined)
    let currentPos = start;
    for (const span of overlapping.sort((a, b) => a.start - b.start)) {
      if (span.start > currentPos) {
        // Gap exists - check if first value matches undefined
        if (firstValue !== undefined) {
          return { uniform: false };
        }
      }
      currentPos = Math.max(currentPos, span.end);
    }
    if (currentPos < end && firstValue !== undefined) {
      return { uniform: false };
    }

    return { uniform: true, value: firstValue };
  }

  // ============================================================================
  // Private Helper Methods
  // ============================================================================

  /**
   * Split spans at the boundaries of [start, end)
   */
  private static splitOverlappingSpans(
    spans: FormattingSpan[],
    start: number,
    end: number
  ): FormattingSpan[] {
    const result: FormattingSpan[] = [];

    for (const span of spans) {
      if (span.end <= start || span.start >= end) {
        // No overlap - keep span as-is
        result.push(span);
      } else {
        // Has overlap - may need to split
        if (span.start < start) {
          // Part before range
          result.push({
            ...span,
            id: span.id,
            end: start
          });
        }
        if (span.start < end && span.end > start) {
          // Part inside range (or overlapping)
          result.push({
            ...span,
            id: uuidv4(), // New ID for split part
            start: Math.max(span.start, start),
            end: Math.min(span.end, end)
          });
        }
        if (span.end > end) {
          // Part after range
          result.push({
            ...span,
            id: uuidv4(), // New ID for split part
            start: end
          });
        }
      }
    }

    return result;
  }

  /**
   * Apply formatting to all spans in range, or create new span
   */
  private static applyToRange(
    spans: FormattingSpan[],
    start: number,
    end: number,
    formatting: FormattingAttributes
  ): FormattingSpan[] {
    // Find spans that are exactly in the range
    let foundInRange = false;

    const updatedSpans = spans.map(span => {
      if (span.start >= start && span.end <= end) {
        // Span is within range - apply formatting
        foundInRange = true;
        return {
          ...span,
          ...formatting
        };
      }
      return span;
    });

    // If no spans covered the range, create a new one
    if (!foundInRange) {
      updatedSpans.push({
        id: uuidv4(),
        start,
        end,
        ...formatting
      });
    }

    return updatedSpans;
  }

  /**
   * Merge adjacent spans with identical formatting
   */
  private static mergeAdjacentSpans(spans: FormattingSpan[]): FormattingSpan[] {
    if (spans.length <= 1) {
      return spans;
    }

    // Sort by start position
    const sorted = [...spans].sort((a, b) => a.start - b.start);
    const result: FormattingSpan[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const previous = result[result.length - 1];

      // Check if adjacent and have identical formatting
      if (previous.end === current.start && spansHaveEqualFormatting(previous, current)) {
        // Merge: extend previous span
        previous.end = current.end;
      } else {
        result.push(current);
      }
    }

    return result;
  }

  /**
   * Apply non-undefined overrides to resolved formatting
   */
  private static applyOverrides(
    resolved: ResolvedFormatting,
    overrides: Partial<ResolvedFormatting>
  ): void {
    if (overrides.fontFamily !== undefined) resolved.fontFamily = overrides.fontFamily;
    if (overrides.fontSizePx !== undefined) resolved.fontSizePx = overrides.fontSizePx;
    if (overrides.fontWeight !== undefined) resolved.fontWeight = overrides.fontWeight;
    if (overrides.fontStyle !== undefined) resolved.fontStyle = overrides.fontStyle;
    if (overrides.color !== undefined) resolved.color = overrides.color;
    if (overrides.backgroundColor !== undefined) resolved.backgroundColor = overrides.backgroundColor;
    if (overrides.underline !== undefined) resolved.underline = overrides.underline;
    if (overrides.strikethrough !== undefined) resolved.strikethrough = overrides.strikethrough;
    if (overrides.superscript !== undefined) resolved.superscript = overrides.superscript;
    if (overrides.subscript !== undefined) resolved.subscript = overrides.subscript;
    if (overrides.features !== undefined) resolved.features = overrides.features;
  }
}
